import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import {
  type AskUserQuestionResponse,
  buildTracifyContextHash,
  getBuiltinDashboardDescriptor,
  type ChatMessage,
  type ChatRuntimeContext,
  type ChatSeedTags,
} from "@tracify/shared";
import { ChatMessageList } from "./chat-message-list";
import { MultimodalInput } from "./multi-modal-input";
import {
  chatSeedInputAtom,
  chatLoadingInputAtom,
  chatModalSeedTagsAtom,
  dashboardModeAtom,
} from "@/atoms";
import { useAtomValue, useSetAtom } from "jotai";
import {
  useAttributionPresetState,
  useCreativeState,
  useDateRangeState,
  useNewVsReturningState,
} from "@lib/hooks/url-state";
import { useGranularity } from "@lib/hooks/use-granularity";
import useSelectedStores from "@lib/hooks/use-selected-stores";
import { cn } from "@/lib/utils";
import { useCurrentDashboardSeedTag } from "@/lib/ai/hooks/use-current-dashboard-seed-tag";
import { useStickyInputOffset } from "./use-sticky-input-offset";
import {
  CHAT_AGENT_QUERY_PARAM,
  canUseChatAgent,
  parseChatAgent,
  type ChatAgent,
} from "@lib/ai/chat-agent";
import { FeatureFlagUtils, useFeatureFlags } from "@lib/hooks/useFeatureFlags";
import { getLatestPendingAskUserQuestion } from "./ask-user-question";
import { TracifyChatProvider, useTracifyChat } from "./use-tracify-chat";

type ChatShellProps = {
  initialSeedInput?: string | null;
  stickyInput?: boolean;
  isDraftRoute?: boolean;
};

type ChatProps = ChatShellProps & {
  chatId?: string;
  initialMessages?: ChatMessage[];
  initialActiveStreamId?: string | null;
  selectedAgent?: ChatAgent;
};

export function mergeCurrentDashboardSeedTags(
  currentDashboardSeedTag: {
    id: string;
    type: "dashboard";
    label: string;
  },
  initialSeedTags?: ChatSeedTags | null,
  currentSeedTags?: ChatSeedTags | null
): ChatSeedTags {
  const initialNonDashboardTags = (initialSeedTags?.tagged ?? []).filter(
    (item) => item.type !== "dashboard"
  );
  const currentNonDashboardTags = (currentSeedTags?.tagged ?? []).filter(
    (item) => item.type !== "dashboard"
  );
  const nonDashboardTags =
    currentNonDashboardTags.length > 0
      ? currentNonDashboardTags
      : initialNonDashboardTags;

  return {
    tagged: [currentDashboardSeedTag, ...nonDashboardTags].slice(0, 12),
  };
}

function ChatShell({
  initialSeedInput,
  stickyInput = false,
  isDraftRoute = false,
}: ChatShellProps) {
  const [input, setInput] = useState("");
  const setChatLoadingInput = useSetAtom(chatLoadingInputAtom);
  const seedTags = useAtomValue(chatModalSeedTagsAtom);
  const setSeedTags = useSetAtom(chatModalSeedTagsAtom);
  const initialSeedTagsRef = useRef(seedTags);
  const seedInput = useAtomValue(chatSeedInputAtom);
  const setSeedInput = useSetAtom(chatSeedInputAtom);
  const currentDashboardSeedTag = useCurrentDashboardSeedTag();
  const lastSeededDashboardKeyRef = useRef<string | null>(null);
  const dashboardMode = useAtomValue(dashboardModeAtom);
  const { creativeChannel } = useCreativeState();
  const { attributionPreset: selectedPresetId } = useAttributionPresetState();
  const {
    startTime: startDate,
    endTime: endDate,
    compared,
    comparePeriod: comparisonPreset,
    previousStartTime: comparisonStartDate,
    previousEndTime: comparisonEndDate,
  } = useDateRangeState();
  const { newVsReturning } = useNewVsReturningState();
  const { effectiveGranularity } = useGranularity();
  const { selectedStoreIds } = useSelectedStores();
  const { inputFormRef, stickyInputOffset } = useStickyInputOffset(stickyInput);
  const {
    chatId,
    messages,
    setMessages,
    sendMessage,
    status,
    stop,
    regenerate,
    pendingAskUserQuestionId,
    submittedAskUserQuestionIds,
    pendingToolApprovalId,
    submitAskUserQuestion,
    handleApproveToolCall,
    handleDenyToolCall,
  } = useTracifyChat();

  useEffect(() => {
    // Abort any in-flight generation when the keyed chat shell is replaced.
    return () => {
      stop();
    };
  }, [stop]);

  const resolvedPresetId = selectedPresetId ?? "";
  const runtimeContext = useMemo(() => {
    const context: ChatRuntimeContext = {};
    const hasComparisonPreset =
      typeof comparisonPreset === "string" && comparisonPreset.length > 0;
    const isNoComparisonPreset = comparisonPreset === "no_comparison";
    const comparisonEnabled = hasComparisonPreset && !isNoComparisonPreset;

    if (resolvedPresetId) {
      context.preset_id = resolvedPresetId;
    }
    if (selectedStoreIds.length > 0) {
      context.store_ids = selectedStoreIds;
    }
    if (startDate) {
      context.start_date = startDate;
    }
    if (endDate) {
      context.end_date = endDate;
    }

    if (comparisonPreset !== undefined) {
      context.comparison_preset = comparisonPreset;
    }
    if (comparisonEnabled && comparisonStartDate !== undefined) {
      context.comparison_start_date = comparisonStartDate;
    }
    if (comparisonEnabled && comparisonEndDate !== undefined) {
      context.comparison_end_date = comparisonEndDate;
    }
    if (newVsReturning !== undefined) {
      context.customer_type = newVsReturning;
    }
    if (effectiveGranularity !== undefined) {
      context.granularity = effectiveGranularity;
    }

    if (
      resolvedPresetId &&
      selectedStoreIds.length > 0 &&
      startDate &&
      endDate
    ) {
      context.scope_key = buildTracifyContextHash({
        selectedPresetId: resolvedPresetId,
        selectedStoreIds,
        selectedStartDate: startDate,
        selectedEndDate: endDate,
        compared: comparisonEnabled ? compared : false,
        comparisonPreset,
        comparisonStartDate: comparisonEnabled
          ? comparisonStartDate
          : undefined,
        comparisonEndDate: comparisonEnabled ? comparisonEndDate : undefined,
        newVsReturning,
        granularity: effectiveGranularity,
      });
    }

    const builtinDashboard =
      currentDashboardSeedTag?.type === "dashboard"
        ? getBuiltinDashboardDescriptor(currentDashboardSeedTag.id, {
            creativeChannel,
            dashboardMode,
          })
        : null;

    if (builtinDashboard) {
      context.default_dashboard = builtinDashboard;
    }

    return context;
  }, [
    compared,
    comparisonEndDate,
    comparisonPreset,
    comparisonStartDate,
    creativeChannel,
    currentDashboardSeedTag,
    dashboardMode,
    effectiveGranularity,
    endDate,
    newVsReturning,
    resolvedPresetId,
    selectedStoreIds,
    startDate,
  ]);

  useEffect(() => {
    const trimmed = initialSeedInput?.trim();
    if (trimmed) {
      setSeedInput(trimmed);
      setChatLoadingInput("");
    }
  }, [initialSeedInput, setSeedInput, setChatLoadingInput]);

  useEffect(() => {
    if (!currentDashboardSeedTag) {
      lastSeededDashboardKeyRef.current = null;
      return;
    }

    const nextKey = `${currentDashboardSeedTag.type}:${currentDashboardSeedTag.id}:${currentDashboardSeedTag.label}`;
    if (lastSeededDashboardKeyRef.current === nextKey) {
      return;
    }

    lastSeededDashboardKeyRef.current = nextKey;

    setSeedTags((currentSeedTags) => {
      return mergeCurrentDashboardSeedTags(
        currentDashboardSeedTag,
        initialSeedTagsRef.current,
        currentSeedTags
      );
    });
  }, [currentDashboardSeedTag, setSeedTags]);

  const showSuggestedActions = messages.length === 0;
  const pendingAskUserQuestion = useMemo(() => {
    const latestPendingAskUserQuestion =
      getLatestPendingAskUserQuestion(messages);
    if (
      latestPendingAskUserQuestion &&
      submittedAskUserQuestionIds.has(latestPendingAskUserQuestion.toolCallId)
    ) {
      return null;
    }

    return latestPendingAskUserQuestion;
  }, [messages, submittedAskUserQuestionIds]);
  const isPendingAskUserQuestionSubmitting =
    pendingAskUserQuestion !== null &&
    pendingAskUserQuestionId === pendingAskUserQuestion.toolCallId;

  const handleSubmitAskUserQuestion = useMemo(
    () =>
      ({
        toolCallId,
        output,
      }: {
        toolCallId: string;
        output: AskUserQuestionResponse;
      }) => {
        void submitAskUserQuestion({
          toolCallId,
          output,
        });
      },
    [submitAskUserQuestion]
  );

  return (
    <div className="relative flex flex-1 min-h-0 flex-col overflow-hidden">
      <div
        className={cn("flex-1 min-h-0 flex flex-col overflow-hidden")}
        style={stickyInput ? { paddingBottom: stickyInputOffset } : undefined}
      >
        <ChatMessageList
          chatId={chatId}
          status={status}
          messages={messages}
          setMessages={setMessages}
          regenerate={regenerate}
          pendingToolApprovalId={pendingToolApprovalId}
          onApproveToolCall={handleApproveToolCall}
          onDenyToolCall={handleDenyToolCall}
        />
      </div>

      <form
        ref={inputFormRef}
        className={cn(
          "shrink-0 flex mx-auto w-full max-w-3xl gap-2 px-3 bg-background pb-3",
          stickyInput &&
            "absolute inset-x-0 bottom-0 z-20 pt-2 pb-4 bg-background/95 supports-[backdrop-filter]:bg-background/80"
        )}
      >
        <MultimodalInput
          input={input}
          setInput={setInput}
          status={status}
          stop={stop}
          messages={messages}
          setMessages={setMessages}
          sendMessage={sendMessage}
          showSuggestedActions={showSuggestedActions}
          seedTags={seedTags}
          seedInput={seedInput}
          runtimeContext={runtimeContext}
          pendingAskUserQuestion={pendingAskUserQuestion}
          isPendingAskUserQuestionSubmitting={
            isPendingAskUserQuestionSubmitting
          }
          onSubmitAskUserQuestion={handleSubmitAskUserQuestion}
          onSeedTagsApplied={() => {
            const appliedSeedTags = seedTags;
            setSeedTags((currentSeedTags) =>
              currentSeedTags === appliedSeedTags ? null : currentSeedTags
            );
          }}
          onSeedInputApplied={() => setSeedInput(null)}
          chatId={chatId}
          isDraftRoute={isDraftRoute}
        />
      </form>
    </div>
  );
}

export default function Chat({
  chatId,
  initialMessages,
  initialActiveStreamId,
  initialSeedInput,
  stickyInput = false,
  isDraftRoute = false,
  selectedAgent,
}: ChatProps) {
  const searchParams = useSearchParams();
  const { flags } = useFeatureFlags();
  const chatAgentAvailability = {
    mediaBuyingAgentEnabled: FeatureFlagUtils.isMediaBuyingAgentEnabled(flags),
  };

  if (chatId) {
    const selectedAvailableAgent =
      selectedAgent && canUseChatAgent(selectedAgent, chatAgentAvailability)
        ? selectedAgent
        : undefined;
    const resolvedAgent =
      selectedAvailableAgent ??
      parseChatAgent(
        searchParams?.get(CHAT_AGENT_QUERY_PARAM),
        chatAgentAvailability
      );

    return (
      <TracifyChatProvider
        chatId={chatId}
        initialActiveStreamId={initialActiveStreamId}
        initialMessages={initialMessages}
        selectedAgent={resolvedAgent}
      >
        <ChatShell
          initialSeedInput={initialSeedInput}
          stickyInput={stickyInput}
          isDraftRoute={isDraftRoute}
        />
      </TracifyChatProvider>
    );
  }

  return (
    <ChatShell
      initialSeedInput={initialSeedInput}
      stickyInput={stickyInput}
      isDraftRoute={isDraftRoute}
    />
  );
}
